import { EPaymentMethod, PaymentMethod } from "../endpoints/payment-methods.endpoints";
import { FiatToken } from "../tokens";
import { RampDirection } from "../types/rampDirection";
import { DestinationType, Networks } from "./networks";

const COUNTRY_TO_PAYMENT_METHOD: Record<string, PaymentMethod> = {
  AR: EPaymentMethod.CBU,
  BR: EPaymentMethod.PIX,
  EU: EPaymentMethod.SEPA
};

/**
 * Derives the 'from' and 'to' destinations based on country code, network, and ramp type
 * @param countryCode - ISO country code (e.g., 'BR', 'AR', 'EU')
 * @param network - Target blockchain network
 * @param rampType - Direction of the ramp (BUY or SELL)
 * @returns Object containing 'from' and 'to' destinations
 */
export function deriveFromTo(
  countryCode: string,
  network: Networks,
  rampType: RampDirection
): { from: DestinationType; to: DestinationType } {
  const normalizedCountryCode = countryCode.toUpperCase();
  const paymentMethod = COUNTRY_TO_PAYMENT_METHOD[normalizedCountryCode];

  if (!paymentMethod) {
    throw new Error(
      `Unsupported country code: ${countryCode}. Supported codes: ${Object.keys(COUNTRY_TO_PAYMENT_METHOD).join(", ")}`
    );
  }

  if (rampType === RampDirection.BUY) {
    return {
      from: paymentMethod,
      to: network
    };
  } else {
    return {
      from: network,
      to: paymentMethod
    };
  }
}

/**
 * Extracts the payment method from the 'from' or 'to' destinations
 * @param from - Source destination
 * @param to - Target destination
 * @returns The payment method used in the transaction
 */
export function getPaymentMethodFromDestinations(from: DestinationType, to: DestinationType): PaymentMethod {
  // Check if 'from' is a payment method
  const paymentMethods: PaymentMethod[] = [EPaymentMethod.PIX, EPaymentMethod.SEPA, EPaymentMethod.CBU, EPaymentMethod.ACH];

  if (paymentMethods.includes(from as PaymentMethod)) {
    return from as PaymentMethod;
  }

  // Check if 'to' is a payment method
  if (paymentMethods.includes(to as PaymentMethod)) {
    return to as PaymentMethod;
  }

  throw new Error(`Unable to determine payment method from destinations: from=${from}, to=${to}`);
}

export const mapFiatToDestination = (fiatToken: FiatToken): DestinationType => {
  const destinationMap: Record<FiatToken, DestinationType> = {
    ARS: EPaymentMethod.CBU,
    BRL: EPaymentMethod.PIX,
    COP: EPaymentMethod.SPEI,
    EUR: EPaymentMethod.SEPA,
    MXN: EPaymentMethod.SPEI,
    USD: EPaymentMethod.ACH
  };

  return destinationMap[fiatToken] || EPaymentMethod.SEPA;
};
